import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import CommonModal from '../Common/Modal/Modal';
import Button from '../Button/Button';
import CustomSelect from '../FormInputs/CustomSelect/CustomSelect';
import CustomFieldControls from './CustomFieldControls';
import SelectFieldOptions from './SelectFieldOptions';
import PreviewInModal from './PreviewInModal';
import { postCustomField, putCustomField, deleteCustomField } from '../../utils/api/customFields';
import {
  useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges,
} from '../../hooks';
import {
  TYPE, defaultLabels, parseFieldOptions, DELETE_WARNING_TEXT,
} from '../../utils/customFieldsUtils';
import Icon from '../Icons/Icon';

const DEFAULT_FIELD_DATA = {};

const CustomFieldsAddEditModal = ({
  orgId, activeTabName, handleClose, setSelectedDate, numberValue = '', setNumberValue, modelId, formItems, setFormItems, fieldData = DEFAULT_FIELD_DATA, setInitialFormItems,
}) => {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTypeError, setSelectedTypeError] = useState('');
  const [label, setLabel] = useState('');
  const [labelError, setLabelError] = useState('');
  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);

  const initialCustomFieldData = {
    type: fieldData?.type,
    label: fieldData?.label || '',
    active: fieldData?.active || true,
    encrypt: fieldData?.encrypt || false,
    required: fieldData?.required || false,
    options: fieldData?.options || [],
  };

  const modifiedCustomFieldData = useMemo(() => ({
    type: selectedType?.value,
    label,
    active: isActive,
    encrypt: isEncrypted,
    required: isRequired,
    options,
  }), [selectedType, label, isActive, isEncrypted, isRequired, options]);

  const hasUnsavedChanges = !isEqual(initialCustomFieldData, modifiedCustomFieldData);

  useUnsavedChangesWarning(isUnsavedChanges);

  const { showToast } = useToasts();

  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, modifiedCustomFieldData]); // customFieldData - track changes to customFieldData for unsaved changes if isUnsavedChanges was set to false on previous changes

  // Update options when `fieldData` or `selectedType` changes
  useEffect(() => {
    setOptions(parseFieldOptions(fieldData, selectedType));
  }, [fieldData, selectedType]);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: handleClose,
  });

  // Handles field type selection
  const handleTypeChange = (selectedOption) => {
    setSelectedType(selectedOption);
    if (selectedTypeError) setSelectedTypeError('');
    if (selectedOption?.value === 'FormItem::Heading') {
      setIsEncrypted(false);
      setIsRequired(false);
    }
  };

  // Handles changes to field options
  const handleOptionChange = (index, value) => {
    setOptions(prevOptions => {
      const updatedOptions = [...prevOptions];
      updatedOptions[index] = value;
      return updatedOptions;
    });
    if (optionsError) setOptionsError('');
  };

  // Handles label input change
  const handleLabelChange = (e) => {
    setLabel(e.target.value);
    if (labelError) setLabelError('');
  };

  // Add new option for Select/Multiselect fields
  const addOption = () => {
    setOptions([...options, '']);
  };

  // Remove an option from Select/Multiselect fields
  const removeOption = (indexToRemove) => {
    setOptions(prevOptions => {
      const updatedOptions = [...prevOptions];
      updatedOptions.splice(indexToRemove, 1);
      return updatedOptions;
    });
  };

  // Create a new custom field
  const handleCreateCustomField = async () => {
    if (!selectedType) {
      setSelectedTypeError('Select a field type.');
      return;
    }
    if (!label.trim()) {
      setLabelError('A label is required.');
      return;
    }
    const isDuplicateLabel = formItems.some(
      item => item.label.trim().toLowerCase() === label.trim().toLowerCase() && item.id !== fieldData?.id,
    );

    if (isDuplicateLabel) {
      setLabelError('A field with this label already exists.');
      return;
    }
    const isOptionsRequired = selectedType?.value === 'FormItem::Select' || selectedType?.value === 'FormItem::Multiselect';

    if (isOptionsRequired && !options.some(option => option.trim() !== '')) {
      setOptionsError('Please add at least one option for the user to select from.');
      return;
    }
    try {
      const modelFormData = {
        organization_id: orgId,
        name: activeTabName,
        form_items_attributes: [
          {
            type: selectedType?.value,
            label,
            active: isActive,
            required: isRequired,
            encrypt: isEncrypted,
            position: formItems.length,
            options_string: Array.isArray(options) ? options.join(',') : options,
          },
        ],
      };
      setIsSaving(true);
      const response = await postCustomField(modelFormData);
      const responseData = await response.json();

      if (response.ok) {
        const newField = {
          ...responseData.custom_field,
          values: responseData.custom_field.values || [],
        };
        const updatedItems = [...formItems, newField];
        setFormItems(updatedItems);
        setInitialFormItems(updatedItems);
        handleClose();
        showToast({ message: 'Custom field has been created.', type: 'success' });
      } else {
        throw new Error(responseData.error);
      }
    } catch (err) {
      showToast({ message: err.message, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Update an existing custom field
  const handleUpdateCustomField = async () => {
    if (!label.trim()) {
      setLabelError('A label is required.');
      return;
    }
    const isDuplicateLabel = formItems.some(
      item => item.label.trim().toLowerCase() === label.trim().toLowerCase() && item.id !== fieldData?.id,
    );

    if (isDuplicateLabel) {
      setLabelError('A field with this label already exists.');
      return;
    }
    const isOptionsRequired = selectedType?.value === 'FormItem::Select' || selectedType?.value === 'FormItem::Multiselect';

    if (isOptionsRequired && !options.some(option => option.trim() !== '')) {
      setOptionsError('Please add at least one option for the user to select from.');
      return;
    }
    try {
      const updatedFieldData = {
        id: fieldData.id,
        type: selectedType.value,
        label,
        active: isActive,
        encrypt: isEncrypted,
        required: isRequired,
        options_string: Array.isArray(options) ? options.join(',') : options,
      };
      const response = await putCustomField(modelId, orgId, updatedFieldData);
      const responseData = await response.json();

      if (response.ok) {
        showToast({ message: 'Custom field has been updated.', type: 'success' });
        setFormItems(prev => prev.map(item => (item.id === updatedFieldData.id
          ? { ...item, ...updatedFieldData, options }
          : item))); handleClose();
      } else {
        throw new Error(responseData.error || 'Failed to update custom field');
      }
    } catch (err) {
      showToast({ message: err.message, type: 'error' });
    }
  };

  // Load field data when component mounts
  useEffect(() => {
    if (fieldData) {
      setLabel(fieldData.label || '');
      const foundType = TYPE.find(type => type.value === fieldData.type);
      setSelectedType(foundType || null);
      setIsActive(fieldData.active !== undefined ? fieldData.active : true);
      setIsEncrypted(fieldData.encrypt || false);
      setIsRequired(fieldData.required || false);
      setOptions(fieldData.options || ['']);
    }
  }, [fieldData]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedOptions = [...options];
    const [movedOption] = updatedOptions.splice(result.source.index, 1);
    updatedOptions.splice(result.destination.index, 0, movedOption);
    setOptions(updatedOptions);
  };

  const getButtonLabel = () => {
    if (isDeleting) {
      return 'Delete';
    }
    if (fieldData) {
      return 'Save';
    }
    return 'Create';
  };

  const getButtonColor = () => {
    if (isDeleting) {
      return 'primary-delete';
    }
    return 'primary';
  };

  const handleDeleteCustomField = async () => {
    try {
      const response = await deleteCustomField(fieldData.id, orgId);
      const responseData = await response.json();

      if (response.ok) {
        showToast({ message: 'Custom field has been deleted.', type: 'success' });
        setFormItems(prev => prev.filter(item => item.id !== fieldData.id));
        handleClose();
      } else {
        throw new Error(responseData.error || 'Failed to delete custom field');
      }
    } catch (err) {
      showToast({ message: err.message, type: 'error' });
    }
  };

  const handlePrimaryAction = () => {
    if (isDeleting) {
      handleDeleteCustomField();
    } else if (fieldData) {
      handleUpdateCustomField();
    } else {
      handleCreateCustomField();
    }
  };

  const onClick = () => {
    if (isDeleting) {
      setIsDeleting(false);
    } else {
      handleCloseDialog();
    }
  };

  const getFormLabel = (form) => {
    if (form === 'site') return 'site form';
    if (form === 'visit') return 'visit form';
    if (form === 'contact') return 'contact form';
    return '';
  };

  const modalTitle = fieldData
    ? `Edit Custom Field (${getFormLabel(activeTabName)})`
    : `New Custom Field (${getFormLabel(activeTabName)})`;

  const hasData = fieldData?.fieldCount > 0;
  const currentType = selectedType?.value || fieldData?.type;

  const isTextType = currentType === 'FormItem::Text' || currentType === 'FormItem::Line';

  const noTransitionsAvailable = hasData && !isTextType;

  const infoMessage = hasData ? 'This field contains data. More field types are available for empty fields.' : '';

  const typeOptions = infoMessage ? [
    ...TYPE,
    { label: infoMessage, value: 'InfoMessage' },
  ] : TYPE;

  const allowedWhenHasDataTextFamily = ['FormItem::Text', 'FormItem::Line'];

  return (
    <>
      <CommonModal
        title={modalTitle}
        titlePosition="left"
        className="py-0 text-vamos-black"
        headerClassName="h-18.5 pl-5.5"
        closeIconPosition="absolute right-5.5 top-8"
        titleStyle="text-2xl font-bold"
        boxClassName="w-1/2"
        isHeaderDivider
        orgId={orgId}
        handleClose={handleCloseDialog}
        activeTabName={activeTabName}
        overflowY=""
        childrenHeight=""
        dataTestId="modal-title"
      >
        <div className="px-5.5 py-8 overflow-y-auto max-h-[65vh]">
          <div className="border-b border-vamos-blue-gray pb-4">
            <h2 className="font-bold text-lg text-left mb-4">Fields</h2>
            <div className="relative flex items-center">
              <h3 className="text-sm font-medium text-left mb-1">Type</h3>
              <Icon
                name="required_field"
                className="ml-1 mb-3 w-2 h-2"
              />
            </div>
            <CustomSelect
              options={typeOptions}
              value={selectedType ? selectedType.label : ''}
              setValue={handleTypeChange}
              placeholder="select type"
              inputHeight={40}
              fontSize={14}
              isDisabled={noTransitionsAvailable}
              dataTestId="custom-field-type-select"
              isOptionDisabled={(option) => {
                if (option.value === selectedType?.value) return true;
                if (!hasData) return false;
                return !allowedWhenHasDataTextFamily.includes(option.value);
              }}
            />
            {noTransitionsAvailable && <p className="text-vamos-gray  text-left text-xs mt-1">{infoMessage}</p>}
            {selectedTypeError && <p className="text-vamos-red  text-left text-xs mt-1">{selectedTypeError}</p>}

            {selectedType && (
              <SelectFieldOptions
                selectedType={selectedType}
                label={label}
                options={options}
                handleLabelChange={handleLabelChange}
                handleOptionChange={handleOptionChange}
                addOption={addOption}
                removeOption={removeOption}
                defaultLabels={defaultLabels(selectedType)}
                fieldData={fieldData}
                labelError={labelError}
                optionsError={optionsError}
                onDragEnd={handleDragEnd}
              />
            )}
          </div>
          <CustomFieldControls
            isActive={isActive}
            setIsActive={setIsActive}
            isEncrypted={isEncrypted}
            setIsEncrypted={setIsEncrypted}
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            selectedType={selectedType}
          />
          <PreviewInModal
            selectedType={selectedType}
            label={label}
            options={options}
            setSelectedDate={setSelectedDate}
            numberValue={numberValue}
            setNumberValue={setNumberValue}
            fieldData={fieldData}
          />
        </div>
        <div className="border-t p-5.5 border-vamos-blue-gray flex space-x-3 justify-between">
          <div>
            {fieldData && (
              !isDeleting ? (
                <Button
                  label="Delete..."
                  color="secondary-delete"
                  size="md"
                  className="!p-0 !m-0 !justify-start"
                  onClick={() => setIsDeleting(true)}
                  dataTestId="custom-field-delete-request-button"
                />
              ) : (
                <div className="text-sm font-medium text-left text-vamos-red leading-normal">
                  {DELETE_WARNING_TEXT(fieldData.fieldCount, activeTabName, fieldData.label)}
                </div>
              )
            )}
          </div>
          <div className="flex space-x-2.5 items-center">
            <Button label="Cancel" size="md" color="secondary" className="px-6" onClick={onClick} dataTestId="custom-field-cancel-button" />
            <Button
              label={getButtonLabel()}
              size="md"
              color={getButtonColor()}
              onClick={handlePrimaryAction}
              isDisabled={isSaving}
              dataTestId="custom-field-submit-button"
            />
          </div>

        </div>
      </CommonModal>
    </>

  );
};

CustomFieldsAddEditModal.propTypes = {
  orgId: PropTypes.string.isRequired,
  activeTabName: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  numberValue: PropTypes.string,
  setNumberValue: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
  formItems: PropTypes.array.isRequired,
  setFormItems: PropTypes.func.isRequired,
  fieldData: PropTypes.object,
  setInitialFormItems: PropTypes.func.isRequired,
};

export default CustomFieldsAddEditModal;
